import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { fetchOrderById, updateOrderStatus } from '@/store/slices/ordersSlice';
import { systemSettingsApi, ordersApi } from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import Button from '@/components/ui/Button';
import CommissionPaymentModal from '@/components/CommissionPaymentModal';
import ConfirmModal from '@/components/ConfirmModal';
import {
  ArrowLeftIcon,
  TruckIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const SellerOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentOrder, isLoading, error } = useSelector((state: RootState) => state.orders);
  const [isFreeMode, setIsFreeMode] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [buyerContact, setBuyerContact] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      setComponentError(null);
      dispatch(fetchOrderById(parseInt(id)))
        .unwrap()
        .then((order) => {
          console.log('Order fetched successfully:', order);
          if (!order || !order.id) {
            setComponentError('Order data is invalid');
          }
        })
        .catch((err) => {
          console.error('Error fetching order:', err);
          setComponentError(err || 'Failed to load order');
        });
    }
    // Check free mode status
    systemSettingsApi.getFreeMode()
      .then(response => {
        setIsFreeMode(response.data.is_free_mode || false);
      })
      .catch((err) => {
        console.error('Error fetching free mode status:', err);
        setIsFreeMode(false);
      });
  }, [id, dispatch]);

  const handleUpdateStatus = (newStatus: string) => {
    setPendingStatus(newStatus);
    setShowConfirmModal(true);
  };

  const confirmStatusUpdate = () => {
    if (id && pendingStatus) {
      dispatch(updateOrderStatus({ id: parseInt(id), newStatus: pendingStatus }))
        .unwrap()
        .then(() => {
          toast.success(`Order status updated to ${pendingStatus} successfully`);
          setShowConfirmModal(false);
          setPendingStatus(null);
        })
        .catch((err: any) => {
          toast.error(err?.message || 'Failed to update order status');
          setShowConfirmModal(false);
          setPendingStatus(null);
        });
    }
  };

  const handlePayCommission = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    if (id) {
      // Refresh order data
      dispatch(fetchOrderById(parseInt(id)));
      // Fetch buyer contact
      try {
        const response = await ordersApi.getBuyerContact(parseInt(id));
        setBuyerContact(response.data.buyer_contact);
      } catch (err) {
        console.error('Error fetching buyer contact:', err);
      }
    }
  };

  const fetchBuyerContact = async () => {
    if (!id || !currentOrder) return;
    
    // Only fetch if commission is paid and contact is released
    if (currentOrder.commission_paid && currentOrder.buyer_contact_released) {
      try {
        const response = await ordersApi.getBuyerContact(parseInt(id));
        setBuyerContact(response.data.buyer_contact);
      } catch (err) {
        console.error('Error fetching buyer contact:', err);
      }
    }
  };

  useEffect(() => {
    if (currentOrder) {
      fetchBuyerContact();
    }
  }, [currentOrder]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-purple-100 text-purple-800';
      case 'shipped':
        return 'bg-indigo-100 text-indigo-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (componentError || error || !currentOrder) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/seller/orders')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Orders
          </button>
        </div>

        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center max-w-md">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h2>
            <p className="text-gray-600 mb-6">
              {componentError || error || 'The order you are looking for does not exist or you do not have permission to view it.'}
            </p>
            <Button onClick={() => navigate('/dashboard/seller/orders')}>
              Back to Orders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Ensure order has required fields
  if (!currentOrder || !currentOrder.order_number || !currentOrder.id) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/seller/orders')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Orders
          </button>
        </div>
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Order Data</h2>
            <p className="text-gray-600 mb-6">The order data is incomplete. Please try again.</p>
            <Button onClick={() => navigate('/dashboard/seller/orders')}>
              Back to Orders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/seller/orders')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Orders
          </button>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Order Details</h1>
          <p className="mt-2 text-gray-600">Order #{currentOrder?.order_number || id || 'N/A'}</p>
        </div>

      {/* Order Status Card */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Order Status</h2>
            <p className="text-sm text-gray-600 mt-1">Current status of your order</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentOrder?.order_status || 'pending')}`}>
            {currentOrder?.order_status || 'Unknown'}
          </span>
        </div>

        {/* Status Actions - Disabled if commission payment required but not paid */}
        {currentOrder?.requires_commission_payment && !currentOrder?.commission_paid && !isFreeMode ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <ExclamationTriangleIcon className="h-5 w-5 inline mr-2" />
              Order status cannot be changed until commission is paid. Please pay the commission to proceed.
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {currentOrder?.order_status === 'pending' && (
              <Button size="sm" onClick={() => handleUpdateStatus('confirmed')}>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Confirm Order
              </Button>
            )}
            {currentOrder?.order_status === 'confirmed' && (
              <Button size="sm" onClick={() => handleUpdateStatus('processing')}>
                <ClockIcon className="h-4 w-4 mr-2" />
                Start Processing
              </Button>
            )}
            {currentOrder?.order_status === 'processing' && (
              <Button size="sm" onClick={() => handleUpdateStatus('shipped')}>
                <TruckIcon className="h-4 w-4 mr-2" />
                Mark as Shipped
              </Button>
            )}
            {currentOrder?.order_status === 'shipped' && (
              <Button size="sm" onClick={() => handleUpdateStatus('delivered')}>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Mark as Delivered
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Order Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Order Number:</span>
              <span className="font-medium text-gray-900">{currentOrder?.order_number || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Order Date:</span>
              <span className="font-medium text-gray-900">
                {currentOrder?.created_at ? new Date(currentOrder.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Payment Status:</span>
              <span className={`font-medium ${
                currentOrder?.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {currentOrder?.payment_status || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Amount:</span>
              <span className="font-bold text-gray-900">{formatCurrency(Number(currentOrder?.total_amount || 0))}</span>
            </div>
          </div>
        </div>

        {/* Shipping Information - Only show if commission paid or free mode */}
        {(isFreeMode || currentOrder?.buyer_contact_released || currentOrder?.commission_paid) && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Information</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Shipping Address</p>
                  <p className="text-gray-900">{currentOrder?.shipping_address || 'N/A'}</p>
                </div>
              </div>
              {isFreeMode && currentOrder?.billing_address && (
                <div className="flex items-start">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Billing Address</p>
                    <p className="text-gray-900">{currentOrder.billing_address}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start">
                <TruckIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Shipping Method</p>
                  <p className="text-gray-900 capitalize">{currentOrder?.shipping_method || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Hidden Shipping Info Message - Show when commission not paid */}
        {!isFreeMode && currentOrder?.requires_commission_payment && !currentOrder?.commission_paid && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Information</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                Shipping address and buyer contact information will be available after commission payment.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Commission Payment Section */}
      {currentOrder?.requires_commission_payment && !currentOrder?.commission_paid && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-start">
            <BellIcon className="h-6 w-6 text-yellow-600 mr-3 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Commission Payment Required</h3>
              <p className="text-gray-700 mb-4">
                You need to pay a commission of <span className="font-bold text-primary-600">
                  {formatCurrency(Number(currentOrder?.platform_commission || 0))}
                </span> to confirm this order and receive buyer contact information.
              </p>
              <Button onClick={handlePayCommission} variant="secondary">
                <CreditCardIcon className="h-5 w-5 mr-2" />
                Pay Commission Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Commission Paid Status */}
      {currentOrder?.commission_paid && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <p className="text-sm text-green-800">
              Commission paid on {currentOrder?.commission_paid_at ? 
                new Date(currentOrder.commission_paid_at).toLocaleString() : 'N/A'}. 
              Order confirmed and buyer contact released.
            </p>
          </div>
        </div>
      )}

      {/* Buyer Contact Information - Show in free mode or if commission paid */}
      {(isFreeMode || currentOrder?.buyer_contact_released || buyerContact) && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Buyer Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Buyer Name</p>
                <p className="text-gray-900">{buyerContact?.name || currentOrder?.buyer_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="text-gray-900">{buyerContact?.email || currentOrder?.buyer_email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <PhoneIcon className="h-5 w-5 text-gray-400 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="text-gray-900">{buyerContact?.phone || currentOrder?.phone_number || 'N/A'}</p>
              </div>
            </div>
            {(currentOrder?.shipping_address || buyerContact?.address) && (
              <div className="flex items-start md:col-span-2">
                <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Delivery Address</p>
                  <p className="text-gray-900">{currentOrder?.shipping_address || buyerContact?.address || 'N/A'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Items */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
        <div className="space-y-4">
          {currentOrder?.items && Array.isArray(currentOrder.items) && currentOrder.items.length > 0 ? (
            currentOrder.items.map((item: any, index: number) => (
              <div key={item.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div>
                    <p className="font-medium text-gray-900">
                      {item.product?.name || item.product_name || `Product #${item.product_id || 'N/A'}`}
                    </p>
                    <p className="text-sm text-gray-600">Quantity: {item.quantity || 0}</p>
                    <p className="text-sm text-gray-500">Unit Price: {formatCurrency(Number(item.unit_price || 0))}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(Number(item.total_price || (item.unit_price || 0) * (item.quantity || 0) || 0))}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-600">No items found in this order.</p>
          )}
        </div>
      </div>

      {/* Free Mode Notice */}
      {isFreeMode && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-2">Free Mode Active</h3>
          <p className="text-green-700">The platform is currently in free mode. No commission is required, and you have direct access to buyer contact information.</p>
        </div>
      )}

      {/* Commission Payment Modal */}
      {showPaymentModal && currentOrder && (
        <CommissionPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          order={{
            id: currentOrder.id,
            order_number: currentOrder.order_number || '',
            total_amount: Number(currentOrder.total_amount || 0),
            platform_commission: Number(currentOrder.platform_commission || 0),
            requires_commission_payment: currentOrder.requires_commission_payment,
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Confirm Status Update Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingStatus(null);
        }}
        onConfirm={confirmStatusUpdate}
        title="Update Order Status"
        message={pendingStatus ? `Are you sure you want to update this order to ${pendingStatus}?` : ''}
        confirmText="Update Status"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={isLoading}
      />
    </div>
  );
};

export default SellerOrderDetailPage;

